import { create } from 'zustand';
import type { SearchResult, Coordinates, Place } from '@/types';

interface SearchState {
  // Search input
  query: string;
  isSearching: boolean;

  // Results
  results: SearchResult[];
  recentSearches: SearchResult[];
  selectedResult: SearchResult | null;

  // Place details
  selectedPlace: Place | null;
  isLoadingPlace: boolean;

  // Suggestions
  suggestions: SearchResult[];
  showSuggestions: boolean;

  // Focus location for search bias
  searchFocus: Coordinates | null;

  // Actions
  setQuery: (query: string) => void;
  setIsSearching: (searching: boolean) => void;
  setResults: (results: SearchResult[]) => void;
  addRecentSearch: (result: SearchResult) => void;
  clearRecentSearches: () => void;
  setSelectedResult: (result: SearchResult | null) => void;
  setSelectedPlace: (place: Place | null) => void;
  setIsLoadingPlace: (loading: boolean) => void;
  setSuggestions: (suggestions: SearchResult[]) => void;
  setShowSuggestions: (show: boolean) => void;
  setSearchFocus: (location: Coordinates | null) => void;
  clearSearch: () => void;
}

const MAX_RECENT_SEARCHES = 10;

export const useSearchStore = create<SearchState>((set) => ({
  // Initial state
  query: '',
  isSearching: false,
  results: [],
  recentSearches: loadRecentSearches(),
  selectedResult: null,
  selectedPlace: null,
  isLoadingPlace: false,
  suggestions: [],
  showSuggestions: false,
  searchFocus: null,

  // Actions
  setQuery: (query) => set({ query }),

  setIsSearching: (searching) => set({ isSearching: searching }),

  setResults: (results) => set({ results, showSuggestions: false }),

  addRecentSearch: (result) => set((state) => {
    const filtered = state.recentSearches.filter((r) => r.id !== result.id);
    const updated = [result, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    saveRecentSearches(updated);
    return { recentSearches: updated };
  }),

  clearRecentSearches: () => {
    localStorage.removeItem('tensormaps-recent-searches');
    set({ recentSearches: [] });
  },

  setSelectedResult: (result) => set({ selectedResult: result }),

  setSelectedPlace: (place) => set({ selectedPlace: place }),

  setIsLoadingPlace: (loading) => set({ isLoadingPlace: loading }),

  setSuggestions: (suggestions) => set({ suggestions }),

  setShowSuggestions: (show) => set({ showSuggestions: show }),

  setSearchFocus: (location) => set({ searchFocus: location }),

  clearSearch: () => set({
    query: '',
    results: [],
    suggestions: [],
    showSuggestions: false,
    selectedResult: null,
  }),
}));

function loadRecentSearches(): SearchResult[] {
  try {
    const stored = localStorage.getItem('tensormaps-recent-searches');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveRecentSearches(searches: SearchResult[]): void {
  try {
    localStorage.setItem('tensormaps-recent-searches', JSON.stringify(searches));
  } catch {
    // Ignore storage errors
  }
}
